import { format, parseISO } from 'date-fns';
import {
  Ban,
  CalendarClock,
  CircleCheck,
  CircleX,
  Clock,
  Send,
  TriangleAlert,
  XCircle,
} from 'lucide-react';

import { cn } from '@/lib/shadcn/utils';
import type {
  CoordinationProcessDetailActionApi,
  CoordinationProcessEventApi,
} from '@/modules/coordination/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import { EmptyState } from '@/modules/ui/components/empty-state/EmptyState';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import type { components } from '@/types/apiSchema';

type CoordinationActionStatus =
  components['schemas']['CoordinationActionStatus'];
type CoordinationActionKind = components['schemas']['CoordinationActionKind'];
type CoordinationEventType = components['schemas']['CoordinationEventType'];

const actionStatusConfig: Record<
  CoordinationActionStatus,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    className: string;
  }
> = {
  Pending: {
    label: 'Pending',
    icon: Clock,
    className: 'bg-muted text-muted-foreground',
  },
  Scheduled: {
    label: 'Scheduled',
    icon: CalendarClock,
    className: 'bg-secondary-500/10 text-secondary-500',
  },
  Sent: {
    label: 'Sent',
    icon: Send,
    className: 'bg-primary/10 text-primary',
  },
  Cancelled: {
    label: 'Cancelled',
    icon: Ban,
    className: 'bg-neutral-300 text-neutral-650',
  },
  Completed: {
    label: 'Completed',
    icon: CircleCheck,
    className: 'bg-semantic-560/10 text-semantic-560',
  },
  Failed: {
    label: 'Failed',
    icon: XCircle,
    className: 'bg-semantic-550/10 text-semantic-550',
  },
  InReview: {
    label: 'Needs review',
    icon: TriangleAlert,
    className: 'bg-semantic-540/10 text-semantic-540',
  },
  Rejected: {
    label: 'Rejected',
    icon: CircleX,
    className: 'bg-semantic-550/10 text-semantic-550',
  },
};

const actionKindLabels: Record<CoordinationActionKind, string> = {
  OutboundMessage: 'Outbound message',
  FinishedCallback: 'Coordination step completed',
  FailedCallback: 'Coordination step failed',
  CreateMeeting: 'Create meeting',
  InviteMeetingParticipant: 'Invite participant',
};

const batchSummaryActionKinds = new Set<CoordinationActionKind>([
  'FinishedCallback',
  'FailedCallback',
]);

const eventTypeLabels: Record<CoordinationEventType, string> = {
  MessageReceived: 'Message received',
  MessageHistoryInit: 'Message history initialized',
  AvailabilityShared: 'Availability shared',
  CandidateSubmitted: 'Candidate submitted',
  CandidateRejected: 'Candidate rejected',
  CandidateEmailSubmitted: 'Candidate email submitted',
  MeetingParticipantResponded: 'Meeting participant responded',
  CalendarConflictDetected: 'Calendar conflict detected',
  CalendarConflictCleared: 'Calendar conflict cleared',
  AvailabilityWithdrawn: 'Availability withdrawn',
};

type ActivityItem =
  | {
      type: 'action';
      timestamp: number;
      action: CoordinationProcessDetailActionApi;
    }
  | { type: 'event'; timestamp: number; event: CoordinationProcessEventApi };

function buildActivityItems(
  actions: CoordinationProcessDetailActionApi[],
  events: CoordinationProcessEventApi[],
): ActivityItem[] {
  const actionItems: ActivityItem[] = actions.map((action) => ({
    type: 'action',
    timestamp: parseISO(action.createdAt).getTime(),
    action,
  }));

  const eventItems: ActivityItem[] = events.map((event) => ({
    type: 'event',
    timestamp: parseISO(event.createdAt).getTime(),
    event,
  }));

  return [...actionItems, ...eventItems].sort(
    (a, b) => b.timestamp - a.timestamp,
  );
}

interface CoordinationActivityTimelineProps {
  actions: CoordinationProcessDetailActionApi[];
  events: CoordinationProcessEventApi[];
  onApprove: (actionId: string) => void;
  onReject: (actionId: string) => void;
  isUpdatingAction: boolean;
}

export function CoordinationActivityTimeline({
  actions,
  events,
  onApprove,
  onReject,
  isUpdatingAction,
}: CoordinationActivityTimelineProps) {
  if (actions.length === 0 && events.length === 0) {
    return (
      <EmptyState
        title="No activity yet"
        description="Actions and events for this candidate will show up here."
      />
    );
  }

  const items = buildActivityItems(actions, events);

  return (
    <div className="bg-card min-w-0 rounded-xl border p-5">
      <Subheading variant="h5" className="mb-4">
        Activity
      </Subheading>
      <div>
        {items.map((item, index) => (
          <ActivityTimelineItem
            key={item.type === 'action' ? item.action.id : item.event.id}
            item={item}
            isLast={index === items.length - 1}
            onApprove={onApprove}
            onReject={onReject}
            isUpdatingAction={isUpdatingAction}
          />
        ))}
      </div>
    </div>
  );
}

interface ActivityTimelineItemProps {
  item: ActivityItem;
  isLast: boolean;
  onApprove: (actionId: string) => void;
  onReject: (actionId: string) => void;
  isUpdatingAction: boolean;
}

function ActivityTimelineItem({
  item,
  isLast,
  onApprove,
  onReject,
  isUpdatingAction,
}: ActivityTimelineItemProps) {
  const isAction = item.type === 'action';
  const isBatchSummary =
    isAction && batchSummaryActionKinds.has(item.action.kind);
  const createdAt = isAction ? item.action.createdAt : item.event.createdAt;

  return (
    <div className="relative flex gap-4">
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'mt-1.5 size-3 shrink-0 rounded-full border-2 bg-white',
            isAction ? 'border-primary' : 'border-secondary-500',
          )}
        />
        {!isLast && <div className="min-h-8 w-0.5 flex-1 bg-neutral-300" />}
      </div>

      <div className="min-w-0 flex-1 pb-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            size="sm"
            variant="outline"
            className={
              isBatchSummary
                ? 'border-neutral-400 text-neutral-500'
                : isAction
                  ? 'border-primary text-primary'
                  : 'border-secondary-500 text-secondary-500'
            }
          >
            {isAction ? 'Action' : 'Event'}
          </Badge>

          {isAction ? (
            <ActionSummary action={item.action} />
          ) : (
            <Subheading variant="h6">
              {eventTypeLabels[item.event.type]}
            </Subheading>
          )}

          <Paragraph variant="caption" className="ml-auto shrink-0">
            {format(parseISO(createdAt), 'MMM d, h:mm a')}
          </Paragraph>

          {isAction && item.action.status === 'InReview' && (
            <div className="mt-2 flex w-full items-center gap-2">
              <Button
                size="sm"
                disabled={isUpdatingAction}
                onClick={() => onApprove(item.action.id)}
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={isUpdatingAction}
                onClick={() => onReject(item.action.id)}
              >
                Reject
              </Button>
            </div>
          )}
        </div>

        {isAction && item.action.payload != null && (
          <pre className="bg-muted text-muted-foreground mt-2 overflow-auto rounded-md border p-3 text-xs">
            {JSON.stringify(item.action.payload, null, 2)}
          </pre>
        )}
        {!isAction && item.event.payload != null && (
          <pre className="bg-muted text-muted-foreground mt-2 overflow-auto rounded-md border p-3 text-xs">
            {JSON.stringify(item.event.payload, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

interface ActionSummaryProps {
  action: CoordinationProcessDetailActionApi;
}

function ActionSummary({ action }: ActionSummaryProps) {
  const {
    label,
    icon: StatusIcon,
    className,
  } = actionStatusConfig[action.status];
  const isBatchSummary = batchSummaryActionKinds.has(action.kind);

  return (
    <>
      {isBatchSummary ? (
        <Paragraph variant="caption" className="text-[11px] text-neutral-500">
          {actionKindLabels[action.kind]}
        </Paragraph>
      ) : (
        <Subheading variant="h6">{actionKindLabels[action.kind]}</Subheading>
      )}
      <Badge size="sm" className={cn('border-transparent', className)}>
        <StatusIcon className="size-3" />
        {label}
      </Badge>
    </>
  );
}
